import { useEffect, useRef, useState } from "react";
import { imageUrl, ptApi, ptError } from "@/pathlync/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Square, Circle, MoveUpRight, Trash2, Save, Undo2 } from "lucide-react";

const TOOLS = [
  { key: "box", label: "Box", icon: Square },
  { key: "circle", label: "Circle", icon: Circle },
  { key: "arrow", label: "Arrow", icon: MoveUpRight },
];

const shapeFrom = (tool, from, to, label) => ({
  kind: tool,
  x: tool === "arrow" ? from.x : Math.min(from.x, to.x),
  y: tool === "arrow" ? from.y : Math.min(from.y, to.y),
  w: tool === "arrow" ? to.x - from.x : Math.abs(to.x - from.x),
  h: tool === "arrow" ? to.y - from.y : Math.abs(to.y - from.y),
  label,
});

/**
 * Draws the pathologist's marks over an image. Coordinates are stored as 0-1 fractions of the
 * image itself, so the drawing surface must be exactly the rendered image: the wrapper is
 * inline-block around the <img> with no letterboxing, otherwise marks drift in the report.
 */
export const ImageAnnotator = ({ image, editable, onSaved }) => {
  const boxRef = useRef(null);
  const [tool, setTool] = useState("box");
  const [shapes, setShapes] = useState(image.annotation_shapes || []);
  const [note, setNote] = useState(image.annotation_note || "");
  const [label, setLabel] = useState("");
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setShapes(image.annotation_shapes || []);
    setNote(image.annotation_note || "");
  }, [image.id, image.annotation_shapes, image.annotation_note]);

  const point = (e) => {
    const r = boxRef.current.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };

  const down = (e) => {
    if (!editable) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const p = point(e);
    setDrag({ from: p, to: p });
  };
  const move = (e) => { if (drag) setDrag((d) => ({ ...d, to: point(e) })); };
  const up = () => {
    if (!drag) return;
    const { from, to } = drag;
    setDrag(null);
    if (Math.abs(to.x - from.x) < 0.01 && Math.abs(to.y - from.y) < 0.01) return;
    setShapes((s) => [...s, shapeFrom(tool, from, to, label.trim())]);
    setLabel("");
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await ptApi.put(`/images/${image.id}/annotations`, { shapes, note });
      toast.success("Annotations saved");
      onSaved?.(data);
    } catch (e) { toast.error(ptError(e)); } finally { setBusy(false); }
  };

  const preview = drag
    ? [...shapes, shapeFrom(tool, drag.from, drag.to, label.trim())]
    : shapes;

  return (
    <div data-testid={`annotator-${image.id}`}>
      {editable && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {TOOLS.map(({ key, label: name, icon: Icon }) => (
            <button key={key} onClick={() => setTool(key)} data-testid={`annotate-tool-${key}`}
                    className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1.5 text-xs font-semibold transition-colors ${
                      tool === key ? "border-[#1e3a8a] bg-[#1e3a8a] text-white"
                        : "border-slate-300 text-slate-600 hover:border-[#1e3a8a]"}`}>
              <Icon size={13} /> {name}
            </button>
          ))}
          <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
                 data-testid="annotate-label-input" placeholder="Label for the next mark (optional)"
                 className="h-8 w-56 text-xs" />
          <button onClick={() => setShapes((s) => s.slice(0, -1))} disabled={!shapes.length}
                  data-testid="annotate-undo-button"
                  className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-40">
            <Undo2 size={13} /> Undo
          </button>
          <button onClick={() => setShapes([])} disabled={!shapes.length}
                  data-testid="annotate-clear-button"
                  className="inline-flex items-center gap-1 rounded border border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-600 disabled:opacity-40">
            <Trash2 size={13} /> Clear
          </button>
        </div>
      )}

      <div className="flex justify-center bg-slate-900 p-2">
        <div ref={boxRef} onPointerDown={down} onPointerMove={move} onPointerUp={up}
             onPointerCancel={up} style={{ touchAction: "none" }}
             data-testid={`annotate-canvas-${image.id}`}
             className={`relative inline-block max-w-full select-none ${
               editable ? "cursor-crosshair" : ""}`}>
          <img src={imageUrl(image.id)} alt={image.caption || image.kind} draggable={false}
               className="block max-h-[70vh] w-auto max-w-full" />
          <svg className="pointer-events-none absolute inset-0 h-full w-full">
            <defs>
              <marker id="pt-arrow" markerWidth="5" markerHeight="5" refX="4" refY="2.5"
                      orient="auto" markerUnits="strokeWidth">
                <path d="M0,0 L5,2.5 L0,5 Z" fill="#dc2626" />
              </marker>
            </defs>
            {preview.map((s, i) => {
              const x = `${s.x * 100}%`;
              const y = `${s.y * 100}%`;
              if (s.kind === "box") {
                return <rect key={i} x={x} y={y} width={`${s.w * 100}%`} height={`${s.h * 100}%`}
                             fill="none" stroke="#dc2626" strokeWidth="2.5" />;
              }
              if (s.kind === "circle") {
                return <ellipse key={i} cx={`${(s.x + s.w / 2) * 100}%`}
                                cy={`${(s.y + s.h / 2) * 100}%`}
                                rx={`${Math.abs(s.w / 2) * 100}%`} ry={`${Math.abs(s.h / 2) * 100}%`}
                                fill="none" stroke="#dc2626" strokeWidth="2.5" />;
              }
              return <line key={i} x1={x} y1={y} x2={`${(s.x + s.w) * 100}%`}
                           y2={`${(s.y + s.h) * 100}%`} stroke="#dc2626" strokeWidth="2.5"
                           markerEnd="url(#pt-arrow)" />;
            })}
          </svg>
          {preview.filter((s) => s.label).map((s, i) => (
            <span key={`l-${i}`} data-testid="annotation-label"
                  className="pointer-events-none absolute -translate-y-full whitespace-nowrap bg-red-600 px-1.5 py-0.5 text-[11px] font-semibold text-white"
                  style={{
                    left: `${(s.kind === "arrow" ? s.x : Math.min(s.x, s.x + s.w)) * 100}%`,
                    top: `${(s.kind === "arrow" ? s.y : Math.min(s.y, s.y + s.h)) * 100}%`,
                  }}>
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {editable ? (
        <div className="mt-3 space-y-2">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    data-testid={`annotate-note-${image.id}`}
                    placeholder="Note about this image (appears in the report)…" />
          <Button onClick={save} disabled={busy} data-testid={`annotate-save-${image.id}`}
                  className="bg-[#1e3a8a] hover:bg-[#172f6e]">
            <Save size={15} className="mr-2" /> {busy ? "Saving…" : "Save annotations"}
          </Button>
        </div>
      ) : (
        note && <p className="mt-3 border-l-2 border-slate-300 pl-3 text-sm text-slate-700">{note}</p>
      )}
    </div>
  );
};
